<script lang="ts">
export default {
  name: "EmbedIFrame",
};
</script>

<script setup lang="ts">
import placeholderImage from "@/images/vimeo-placeholder.png";
import { embedCodeType } from "@/utils/embed";
import Cookies from "js-cookie";
import { isUser } from "@/stores/user";

type Props = {
  urlOrIframe: string;
  placeholderImageUrl?: string;
  altText?: string;
};
const props = withDefaults(defineProps<Props>(), {
  placeholderImageUrl: placeholderImage,
  altText: "",
});

const { t } = useI18n();
const { apiClient: platformApiClient } = usePlatformApiClient();

const userStore = useUserStore();

const cookieName = "_fobizz_embed_iframe_consent";
const setCookie = () => {
  const isSecure = window.location.protocol === "https:";
  Cookies.set(cookieName, "true", {
    path: "/",
    expires: 365,
    secure: isSecure,
  });
};
const hasCookie = () => {
  return !!Cookies.get(cookieName);
};

const clearCookie = () => Cookies.remove(cookieName);

const embedConsentGivenAt = computed(() => {
  if (isUser(userStore.user)) {
    return userStore.user?.embedConsentGivenAt;
  }

  return undefined;
});

const visibleValue = ref(!!embedConsentGivenAt.value || hasCookie());

const enableAutoplay = ref(false);

const videoUrlWithAutoplay = computed(() => {
  if (!props.urlOrIframe) return undefined;

  const url = new URL(props.urlOrIframe);
  url.searchParams.set("autoplay", enableAutoplay.value ? "1" : "0");
  return url.toString();
});

const show = () => {
  visibleValue.value = true;
  // enable autoplay only on click
  enableAutoplay.value = true;

  // if logged out (visitor) use cookie, otherwise use platform user
  if (!userStore.isUser) {
    setCookie();
  } else {
    updateCurrentUser();
  }
};

const updateCurrentUser = async () => {
  await platformApiClient.users.updateCurrentUser({
    requestBody: { user: { embedConsentGivenAt: new Date().toString() } },
  });
};

watch(
  () => embedConsentGivenAt.value,
  () => {
    // remove overlay if consent given elsewhere
    visibleValue.value = !!embedConsentGivenAt.value;
  }
);

onMounted(() => {
  // if cookie was set when logged out (visitor), then update the now logged in user and remove cookie
  if (userStore.isUser && hasCookie()) {
    updateCurrentUser();
    clearCookie();
  }
});
</script>

<template>
  <div class="embed-content embed-content__container">
    <template v-if="visibleValue">
      <iframe
        v-if="embedCodeType(urlOrIframe) == 'url'"
        :src="videoUrlWithAutoplay"
        title="VideoUrl With Autoplay"
        frameborder="0"
        :allow="`${enableAutoplay ? 'autoplay;' : ''} fullscreen`"
      >
      </iframe>
      <div v-else-if="embedCodeType(urlOrIframe) == 'iframe'">
        <div v-html="urlOrIframe"></div>
      </div>
      <div v-else class="embed-content__placeholder">
        <div class="embed-content__overlay">
          <div class="embed-content__text">
            {{ t("validations.validEmbedCode") }}
          </div>
        </div>
      </div>
    </template>
    <div
      v-else
      class="embed-content__placeholder"
      data-testid="embed-content-placeholder"
    >
      <img :src="placeholderImageUrl" alt="Vimeo Video" />
      <a href="#" class="embed-content__overlay" @click.prevent="show">
        <i class="bi bi-play-circle embed-content__icon"></i>
        <div class="embed-content__text">
          <template v-if="userStore.isPupil">
            {{ t("hints.embedIFramePupil") }}
          </template>
          <template v-else>
            {{ t("hints.embedIFrame") }}
          </template>
        </div>
      </a>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.embed-content {
  &__container {
    position: relative;
    overflow: hidden;
    height: 100%;

    /* 16:9 */
    padding-bottom: 56.25%;

    // see iframe styling down below
  }

  &__placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: auto;
      object-fit: cover;
    }
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: $spacer-3;
    background-color: rgb(0 0 0 / 60%);
    padding: $spacer-3 $spacer-5;
    text-align: center;
    color: $white;
    text-decoration: none;
  }

  &__icon {
    font-size: 3rem;
    line-height: 1.2;
  }

  &__text {
    font-size: $font-size-xs;
    max-width: 500px;
  }

  &--sm &__text {
    font-size: 0.5rem;
  }

  &:hover &__overlay {
    color: $brand-courses;
  }
}
</style>

<style lang="scss">
// necessary to style iframe coming from v-html
.embed-content__container iframe {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
}
</style>
